import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import type { GetToken } from '~/config'
import { createClient } from '~/data/graphql'
import {
  assignAgentToOffice,
  fetchOffices,
  hireAgent,
  joinPulse,
  setAgentRunState,
  updateAgentInstructions,
  type HiredAgent,
} from '~/data/interact'
import type { Pulse } from '~/data/types'
import { theme } from '~/theme'

// Hire an AI agent straight from a "+ hire" desk: name it, give it a role, and
// it's provisioned into the org (real agent account) and seated in this room.

export function HirePanel({
  pulse,
  organizationId,
  getToken,
  onClose,
  compact,
}: {
  pulse: Pulse
  organizationId: string
  getToken: GetToken
  onClose: () => void
  compact?: boolean
}) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [jobTitle, setJobTitle] = useState('')
  const [charter, setCharter] = useState('')
  const [instructions, setInstructions] = useState('')
  const [state, setState] = useState<'idle' | 'hiring' | 'done' | 'error'>('idle')
  const [error, setError] = useState<string | null>(null)
  const [hired, setHired] = useState<HiredAgent | null>(null)
  const [copied, setCopied] = useState(false)
  const [officeNote, setOfficeNote] = useState<string | null>(null)

  // The org's offices — the box runtime that gives an agent a life. If one is
  // active, the hire is auto-assigned there and started.
  const offices = useQuery({
    queryKey: ['offices', organizationId],
    queryFn: async () => fetchOffices(createClient(await getToken()), organizationId),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
  const activeOffices = (offices.data ?? []).filter((o) => o.status === 'ACTIVE')
  const [officeId, setOfficeId] = useState<string | 'none' | null>(null)
  const chosenOfficeId =
    officeId === 'none' ? null : (officeId ?? activeOffices[0]?.id ?? null)

  const hire = async () => {
    if (!name.trim() || state === 'hiring') return
    setState('hiring')
    setError(null)
    try {
      const client = createClient(await getToken())
      const result = await hireAgent(client, {
        organizationId,
        pulseId: pulse.id,
        name: name.trim(),
        jobTitle: jobTitle.trim() || undefined,
        jobDescription: charter.trim() || undefined,
      })
      // Their brain: initial CLAUDE.md, versioned server-side. Provisioning
      // leaves instructions empty — without this the hire is a blank identity.
      if (instructions.trim()) {
        try {
          await updateAgentInstructions(client, {
            agentAccountId: result.agentAccountId,
            content: instructions.trim(),
            note: 'Initial instructions (hired in Glass Office)',
          })
        } catch {
          // Non-fatal: editable later from Manage agent.
        }
      }
      // Give the hire a LIFE: place them in the office box and start them.
      // Without an office an agent account exists but can never run.
      if (chosenOfficeId) {
        const officeName =
          activeOffices.find((o) => o.id === chosenOfficeId)?.name ?? 'the office'
        try {
          await assignAgentToOffice(client, {
            agentAccountId: result.agentAccountId,
            officeId: chosenOfficeId,
          })
          try {
            await setAgentRunState(client, {
              agentAccountId: result.agentAccountId,
              runState: 'RUNNING',
            })
            setOfficeNote(`⚡ In ${officeName} — comes alive on the box's next sync`)
          } catch {
            setOfficeNote(`In ${officeName}, but not started — start it from the dashboard`)
          }
        } catch (e) {
          setOfficeNote(
            `Couldn't place them in ${officeName}${
              e instanceof Error && e.message.includes('seat') ? ' (no free seats)' : ''
            } — assign from the dashboard`,
          )
        }
      } else if (activeOffices.length === 0) {
        setOfficeNote(
          'No office in this org yet — the agent can’t run until a box is set up',
        )
      }
      setHired(result)
      setState('done')
    } catch (e) {
      setState('error')
      setError(e instanceof Error ? e.message : 'Hiring failed')
    }
  }

  const finish = () => {
    // Refetch the snapshot so the new agent takes their seat in the room.
    void queryClient.invalidateQueries({ queryKey: ['snapshot', organizationId] })
    onClose()
  }

  return (
    <aside style={compact ? sheetCompact : sheet}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.cream }}>
            ✦ Hire an AI agent
          </div>
          <div style={{ fontSize: 11.5, color: theme.textDim }}>
            Joins <span style={{ color: theme.terracottaLite }}>{pulse.name}</span> as a
            real teammate
          </div>
        </div>
        <button onClick={state === 'done' ? finish : onClose} style={closeBtn} aria-label="Close">
          ×
        </button>
      </div>

      {state !== 'done' && (
        <>
          <label style={label}>
            Name
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Scout"
              maxLength={80}
              autoFocus
              style={input}
            />
          </label>
          <label style={label}>
            Job title
            <input
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="e.g. Research analyst"
              maxLength={120}
              style={input}
            />
          </label>
          {activeOffices.length > 0 && (
            <label style={label}>
              Office (where they run)
              <select
                value={officeId ?? activeOffices[0]?.id ?? 'none'}
                onChange={(e) => setOfficeId(e.target.value)}
                style={{ ...input, appearance: 'auto' as never }}
              >
                {activeOffices.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
                <option value="none">No office (account only)</option>
              </select>
            </label>
          )}
          {!offices.isLoading && activeOffices.length === 0 && (
            <div style={{ fontSize: 10.5, color: theme.textDim, marginTop: 8 }}>
              No office box in this org yet — the agent will exist but can't run
              until one is set up.
            </div>
          )}
          <label style={label}>
            Role description <span style={{ color: theme.textDim, fontWeight: 400 }}>(optional)</span>
            <textarea
              value={charter}
              onChange={(e) => setCharter(e.target.value)}
              placeholder="What is this agent responsible for?"
              maxLength={2000}
              rows={2}
              style={{ ...input, resize: 'vertical', fontFamily: 'inherit' }}
            />
          </label>
          <label style={label}>
            Instructions <span style={{ color: theme.textDim, fontWeight: 400 }}>(optional — their CLAUDE.md)</span>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="How should this agent work? Markdown that lands on their box."
              maxLength={65535}
              rows={3}
              style={{ ...input, resize: 'vertical', fontFamily: 'ui-monospace, monospace', fontSize: 11 }}
            />
          </label>
          <button
            onClick={() => void hire()}
            disabled={state === 'hiring' || !name.trim()}
            style={{ ...hireBtn, opacity: state === 'hiring' || !name.trim() ? 0.6 : 1 }}
          >
            {state === 'hiring' ? 'Hiring…' : '✦ Hire agent'}
          </button>
          {state === 'error' && (
            <div style={{ fontSize: 11.5, color: '#ff8a8a', marginTop: 6 }}>
              {error ?? 'Hiring failed'} — org owners only.
            </div>
          )}
        </>
      )}

      {state === 'done' && hired && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 13, color: theme.online, fontWeight: 600 }}>
            ✓ {hired.name} hired
            {hired.joinedRoom ? ` — joining ${pulse.name}` : ''}
          </div>
          {officeNote && (
            <div
              style={{
                fontSize: 11.5,
                marginTop: 4,
                color: officeNote.startsWith('⚡') ? '#c9c3ff' : '#ffb86b',
              }}
            >
              {officeNote}
            </div>
          )}
          {!hired.joinedRoom && (
            <div style={{ fontSize: 11.5, color: '#ffb86b', marginTop: 4 }}>
              Hired org-wide, but seating them in {pulse.name} needs a room
              owner/admin.
              {hired.joinError && (
                <div style={{ color: theme.textDim, marginTop: 3, fontSize: 10.5 }}>
                  {hired.joinError.slice(0, 160)}
                </div>
              )}
              <button
                onClick={() => {
                  void (async () => {
                    try {
                      const client = createClient(await getToken())
                      await joinPulse(client, { pulseId: pulse.id, userId: hired.userId })
                      setHired({ ...hired, joinedRoom: true, joinError: null })
                    } catch (e) {
                      setHired({
                        ...hired,
                        joinError: e instanceof Error ? e.message : 'join failed',
                      })
                    }
                  })()
                }}
                style={copyBtn}
              >
                Retry seating
              </button>
            </div>
          )}
          {hired.credential && (
            <div style={credBox}>
              <div style={{ fontSize: 10.5, color: theme.textDim, marginBottom: 4 }}>
                Agent login (shown once here — also revealable in the dashboard):
              </div>
              <div style={mono}>{hired.credential.email}</div>
              <div style={mono}>{hired.credential.password}</div>
              <button
                onClick={() => {
                  void navigator.clipboard
                    .writeText(`${hired.credential?.email}\n${hired.credential?.password}`)
                    .then(() => setCopied(true))
                }}
                style={copyBtn}
              >
                {copied ? 'Copied ✓' : 'Copy credentials'}
              </button>
            </div>
          )}
          <button onClick={finish} style={{ ...hireBtn, marginTop: 10 }}>
            Show them to their desk →
          </button>
        </div>
      )}
    </aside>
  )
}

const sheet: React.CSSProperties = {
  position: 'absolute',
  top: 70,
  right: 18,
  width: 300,
  padding: 16,
  background: theme.panel,
  border: `1px solid ${theme.border}`,
  borderRadius: 12,
  color: theme.text,
}
const sheetCompact: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  // Scroll like every other sheet: the hire form is ~500px tall and the
  // keyboard (Name autofocuses) shrinks phone viewports well below that —
  // without this the × close button clips above the screen.
  maxHeight: '72vh',
  overflowY: 'auto',
  padding: 16,
  paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
  background: theme.panel,
  borderTop: `1px solid ${theme.border}`,
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  color: theme.text,
}
const closeBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: theme.textDim,
  cursor: 'pointer',
  fontSize: 20,
  lineHeight: 1,
  padding: '2px 6px',
}
const label: React.CSSProperties = {
  display: 'block',
  marginTop: 10,
  fontSize: 11.5,
  fontWeight: 600,
  color: theme.textDim,
}
const input: React.CSSProperties = {
  display: 'block',
  width: '100%',
  boxSizing: 'border-box',
  marginTop: 4,
  background: '#101218',
  border: `1px solid ${theme.border}`,
  borderRadius: 8,
  color: theme.text,
  fontSize: 12.5,
  padding: '8px 10px',
  outline: 'none',
}
const hireBtn: React.CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 14,
  background: '#5b54c8',
  color: '#eef0ff',
  border: 'none',
  borderRadius: 9,
  fontSize: 13,
  fontWeight: 700,
  padding: '10px 12px',
  cursor: 'pointer',
}
const credBox: React.CSSProperties = {
  marginTop: 10,
  padding: 10,
  background: '#101218',
  border: `1px solid ${theme.border}`,
  borderRadius: 9,
}
const mono: React.CSSProperties = {
  fontFamily: 'ui-monospace, monospace',
  fontSize: 11.5,
  color: theme.cream,
  overflowWrap: 'anywhere',
  padding: '2px 0',
}
const copyBtn: React.CSSProperties = {
  marginTop: 6,
  background: 'none',
  border: `1px solid ${theme.border}`,
  color: theme.textDim,
  borderRadius: 7,
  fontSize: 11,
  padding: '5px 9px',
  cursor: 'pointer',
}
